/**
 * Audits Page
 * Phase 4: Projects & Audit Runs
 * 
 * Lists all audit runs with filters, pagination, and sorting.
 * Provides navigation to audit details and project context.
 */

import { useState, useEffect, useMemo } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { api, AuditRunListItem } from '../lib/api'
import { LoadingSpinner } from '../components/design-system/Loading'
import { EmptyState } from '../components/design-system/EmptyState'
import { StatusBadge, SeverityBadge } from '../components/design-system/Badge'
import { Button } from '../components/design-system/Button'
import { StartAuditDialog } from '../components/StartAuditDialog'
import { formatRelativeTime } from '../lib/dashboardHelpers'
import { useLocale } from '../contexts/LocaleContext'
import { statusLabel } from '../lib/i18n'

type SortField = 'created_at' | 'project' | 'status' | 'findings' | 'duration'
type SortOrder = 'asc' | 'desc'

export function AuditsPage() {
  const { t, locale } = useLocale()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [audits, setAudits] = useState<AuditRunListItem[]>([])
  const [totalAudits, setTotalAudits] = useState(0)
  
  // Filters and pagination from URL
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '')
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || '')
  const [severityFilter, setSeverityFilter] = useState(searchParams.get('severity') || '')
  const [currentPage, setCurrentPage] = useState(parseInt(searchParams.get('page') || '1', 10))
  const [pageSize] = useState(50)
  const [sortField, setSortField] = useState<SortField>((searchParams.get('sort') as SortField) || 'created_at')
  const [sortOrder, setSortOrder] = useState<SortOrder>((searchParams.get('order') as SortOrder) || 'desc')
  const [startOpen, setStartOpen] = useState(false)
  
  useEffect(() => {
    loadAudits()
  }, [currentPage, statusFilter])
  
  // Update URL when filters change
  useEffect(() => {
    const params: Record<string, string> = {}
    if (searchQuery) params.search = searchQuery
    if (statusFilter) params.status = statusFilter
    if (severityFilter) params.severity = severityFilter
    if (currentPage > 1) params.page = currentPage.toString()
    if (sortField !== 'created_at') params.sort = sortField
    if (sortOrder !== 'desc') params.order = sortOrder
    
    setSearchParams(params)
  }, [searchQuery, statusFilter, severityFilter, currentPage, sortField, sortOrder, setSearchParams])
  
  async function loadAudits() {
    try {
      setLoading(true)
      setError(null)
      
      const data = await api.listAuditRuns({
        status: statusFilter || undefined,
        page: currentPage,
        page_size: pageSize,
      })
      
      setAudits(data.audits)
      setTotalAudits(data.total)
    } catch (err) {
      setError(err instanceof Error ? err.message : t('audits.failed'))
    } finally {
      setLoading(false)
    }
  }
  
  // Client-side search and severity filtering (since API doesn't support these yet)
  const filteredAndSortedAudits = useMemo(() => {
    let result = [...audits]
    
    // Text search
    if (searchQuery) {
      const query = searchQuery.toLowerCase()
      result = result.filter(a => 
        a.project_name.toLowerCase().includes(query) ||
        a.id.toLowerCase().includes(query) ||
        (a.error_message?.toLowerCase().includes(query))
      )
    }
    
    // Severity filter (has critical or high)
    if (severityFilter) {
      if (severityFilter === 'critical') {
        result = result.filter(a => a.critical_count > 0)
      } else if (severityFilter === 'high') {
        result = result.filter(a => a.high_count > 0)
      } else if (severityFilter === 'risk') {
        result = result.filter(a => a.critical_count > 0 || a.high_count > 0)
      }
    }
    
    // Sorting
    result.sort((a, b) => {
      let comparison = 0
      
      switch (sortField) {
        case 'created_at':
          comparison = a.created_at.localeCompare(b.created_at)
          break
        case 'project':
          comparison = a.project_name.localeCompare(b.project_name)
          break
        case 'status':
          comparison = a.status.localeCompare(b.status)
          break
        case 'findings':
          comparison = a.total_findings - b.total_findings
          break
        case 'duration':
          const aDuration = a.duration_seconds || 0
          const bDuration = b.duration_seconds || 0
          comparison = aDuration - bDuration
          break
      }
      
      return sortOrder === 'asc' ? comparison : -comparison
    })
    
    return result
  }, [audits, searchQuery, severityFilter, sortField, sortOrder])
  
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')
    } else {
      setSortField(field)
      setSortOrder('desc')
    }
  }
  
  const uniqueStatuses = useMemo(() => {
    // Define all possible statuses in order
    return ['pending', 'queued', 'running', 'completed', 'partial', 'failed', 'cancelled', 'timeout']
  }, [])
  
  const formatDuration = (seconds: number | null): string => {
    if (seconds === null || seconds === 0) return '—'
    if (seconds < 60) return `${Math.round(seconds)}s`
    if (seconds < 3600) return `${Math.round(seconds / 60)}m`
    return `${Math.round(seconds / 3600)}h`
  }
  
  const totalPages = Math.ceil(totalAudits / pageSize)
  
  // Loading state
  if (loading && currentPage === 1) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        <h1 style={{
          margin: 0,
          fontFamily: 'var(--reviewer-font-mono)',
          fontSize: 'var(--reviewer-text-2xl)',
          fontWeight: 500,
          color: 'var(--reviewer-text)',
        }}>
          {t('nav.audits')}
        </h1>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '320px' }}>
          <LoadingSpinner size="lg" text={t('audits.loading')} />
        </div>
      </div>
    )
  }
  
  // Error state
  if (error) {
    return (
      <div
        style={{
          maxWidth: '600px',
          margin: '0 auto',
          padding: '32px',
        }}
      >
        <div
          style={{
            border: '1px solid var(--reviewer-danger-border)',
            backgroundColor: 'var(--reviewer-danger-soft)',
            padding: '24px',
          }}
        >
          <h2 style={{ 
            margin: 0, 
            marginBottom: '12px',
            fontFamily: 'var(--reviewer-font-mono)',
            fontSize: 'var(--reviewer-text-lg)',
            color: 'var(--reviewer-danger)',
          }}>
            {t('audits.failed')}
          </h2>
          <p style={{ 
            margin: 0, 
            marginBottom: '16px',
            fontFamily: 'var(--reviewer-font-mono)',
            fontSize: 'var(--reviewer-text-sm)',
            color: 'var(--reviewer-text)',
          }}>
            {error}
          </p>
          <Button onClick={loadAudits} variant="danger">
            {t('common.retry')}
          </Button>
        </div>
      </div>
    )
  }
  
  // Empty state
  if (totalAudits === 0) {
    return (
      <div style={{ maxWidth: '800px', margin: '0 auto', padding: '32px 0' }} data-testid="audits-page">
        <h1 style={{
          margin: 0,
          marginBottom: '16px',
          fontFamily: 'var(--reviewer-font-mono)',
          fontSize: 'var(--reviewer-text-2xl)',
          fontWeight: 500,
          color: 'var(--reviewer-text)',
          textAlign: 'center',
        }}>
          {t('nav.audits')}
        </h1>
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '16px' }}>
          <Button onClick={() => setStartOpen(true)} data-testid="start-audit-button">
            {t('audits.start')}
          </Button>
        </div>
        <EmptyState 
          title={t('audits.empty')} 
          description={t('audits.emptyBody')}
          action={{ label: t('audits.start'), onClick: () => setStartOpen(true) }}
        />
        <StartAuditDialog isOpen={startOpen} onClose={() => setStartOpen(false)} />
      </div>
    )
  }
  
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }} data-testid="audits-page">
      {/* Header */}
      <header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '16px' }}>
        <div>
          <h1
            style={{
              margin: 0,
              marginBottom: '8px',
              fontFamily: 'var(--reviewer-font-mono)',
              fontSize: 'var(--reviewer-text-2xl)',
              fontWeight: 500,
              color: 'var(--reviewer-text)',
            }}
          >
            {t('nav.audits')}
          </h1>
          <p
            style={{
              margin: 0,
              fontFamily: 'var(--reviewer-font-mono)',
              fontSize: 'var(--reviewer-text-base)',
              color: 'var(--reviewer-text-secondary)',
            }}
          >
            {t('audits.summary', { n: totalAudits, page: currentPage, total: totalPages, shown: filteredAndSortedAudits.length })}
          </p>
        </div>
        <Button onClick={() => setStartOpen(true)} data-testid="start-audit-button">
          {t('audits.start')}
        </Button>
      </header>

      {/* Filters */}
      <div
        style={{
          display: 'flex',
          gap: '12px',
          flexWrap: 'wrap',
          alignItems: 'center',
          padding: '16px',
          border: '1px solid var(--reviewer-border)',
          backgroundColor: 'var(--reviewer-surface)',
        }}
      >
        {/* Search */}
        <input
          type="text"
          placeholder={t('audits.searchPh')}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          style={{
            flex: '1 1 300px',
            padding: '8px 12px',
            fontFamily: 'var(--reviewer-font-mono)',
            fontSize: 'var(--reviewer-text-sm)',
            backgroundColor: 'var(--reviewer-bg)',
            border: '1px solid var(--reviewer-border)',
            color: 'var(--reviewer-text)',
            outline: 'none',
          }}
          aria-label={t('audits.searchAria')}
        />
        
        {/* Status Filter */}
        <select
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value)
            setCurrentPage(1)
          }}
          style={{
            padding: '8px 12px',
            fontFamily: 'var(--reviewer-font-mono)',
            fontSize: 'var(--reviewer-text-sm)',
            backgroundColor: 'var(--reviewer-bg)',
            border: '1px solid var(--reviewer-border)',
            color: 'var(--reviewer-text)',
            outline: 'none',
          }}
          aria-label={t('audits.filterStatusAria')}
        >
          <option value="">{t('audits.allStatuses')}</option>
            {uniqueStatuses.map(status => (
            <option key={status} value={status}>{statusLabel(locale, status)}</option>
          ))}
        </select>
        
        {/* Severity Filter */}
        <select
          value={severityFilter}
          onChange={(e) => setSeverityFilter(e.target.value)}
          style={{
            padding: '8px 12px',
            fontFamily: 'var(--reviewer-font-mono)',
            fontSize: 'var(--reviewer-text-sm)',
            backgroundColor: 'var(--reviewer-bg)',
            border: '1px solid var(--reviewer-border)',
            color: 'var(--reviewer-text)',
            outline: 'none',
          }}
          aria-label={t('audits.filterSeverityAria')}
        >
          <option value="">{t('audits.allSeverities')}</option>
          <option value="risk">{t('audits.hasRisk')}</option>
          <option value="critical">{t('audits.hasCritical')}</option>
          <option value="high">{t('audits.hasHigh')}</option>
        </select>
        
        {/* Clear Filters */}
        {(searchQuery || statusFilter || severityFilter) && (
          <button
            onClick={() => {
              setSearchQuery('')
              setStatusFilter('')
              setSeverityFilter('')
              setCurrentPage(1)
            }}
            style={{
              padding: '8px 12px',
              fontFamily: 'var(--reviewer-font-mono)',
              fontSize: 'var(--reviewer-text-sm)',
              backgroundColor: 'transparent',
              border: '1px solid var(--reviewer-border)',
              color: 'var(--reviewer-text-secondary)',
              cursor: 'pointer',
            }}
            aria-label={t('audits.clearFilters')}
          >
            {t('audits.clearFilters')}
          </button>
        )}
      </div>

      {/* Audits Table */}
      {filteredAndSortedAudits.length === 0 ? (
        <div style={{ padding: '48px 0', textAlign: 'center' }}>
          <p style={{ 
            margin: 0,
            fontFamily: 'var(--reviewer-font-mono)',
            fontSize: 'var(--reviewer-text-base)',
            color: 'var(--reviewer-text-secondary)',
          }}>
            {t('audits.noMatch')}
          </p>
        </div>
      ) : (
        <div
          style={{
            border: '1px solid var(--reviewer-border)',
            backgroundColor: 'var(--reviewer-surface)',
            overflowX: 'auto',
          }}
        >
          <table
            style={{
              width: '100%',
              borderCollapse: 'collapse',
              fontFamily: 'var(--reviewer-font-mono)',
              fontSize: 'var(--reviewer-text-sm)',
            }}
            role="table"
            aria-label={t('audits.tableAria')}
          >
            <thead>
              <tr style={{ borderBottom: '1px solid var(--reviewer-border)' }}>
                <th
                  onClick={() => handleSort('project')}
                  style={{
                    padding: '12px 16px',
                    textAlign: 'left',
                    fontWeight: 500,
                    color: 'var(--reviewer-text-secondary)',
                    cursor: 'pointer',
                    userSelect: 'none',
                  }}
                  role="columnheader"
                  aria-sort={sortField === 'project' ? (sortOrder === 'asc' ? 'ascending' : 'descending') : 'none'}
                >
                  {t('audits.colProject')} {sortField === 'project' && (sortOrder === 'asc' ? '↑' : '↓')}
                </th>
                <th
                  onClick={() => handleSort('created_at')}
                  style={{
                    padding: '12px 16px',
                    textAlign: 'left',
                    fontWeight: 500,
                    color: 'var(--reviewer-text-secondary)',
                    cursor: 'pointer',
                    userSelect: 'none',
                  }}
                  role="columnheader"
                  aria-sort={sortField === 'created_at' ? (sortOrder === 'asc' ? 'ascending' : 'descending') : 'none'}
                >
                  {t('audits.colCreated')} {sortField === 'created_at' && (sortOrder === 'asc' ? '↑' : '↓')}
                </th>
                <th
                  onClick={() => handleSort('status')}
                  style={{
                    padding: '12px 16px',
                    textAlign: 'left',
                    fontWeight: 500,
                    color: 'var(--reviewer-text-secondary)',
                    cursor: 'pointer',
                    userSelect: 'none',
                  }}
                  role="columnheader"
                  aria-sort={sortField === 'status' ? (sortOrder === 'asc' ? 'ascending' : 'descending') : 'none'}
                >
                  {t('audits.colStatus')} {sortField === 'status' && (sortOrder === 'asc' ? '↑' : '↓')}
                </th>
                <th
                  onClick={() => handleSort('findings')}
                  style={{
                    padding: '12px 16px',
                    textAlign: 'center',
                    fontWeight: 500,
                    color: 'var(--reviewer-text-secondary)',
                    cursor: 'pointer',
                    userSelect: 'none',
                  }}
                  role="columnheader"
                  aria-sort={sortField === 'findings' ? (sortOrder === 'asc' ? 'ascending' : 'descending') : 'none'}
                >
                  {t('audits.colFindings')} {sortField === 'findings' && (sortOrder === 'asc' ? '↑' : '↓')}
                </th>
                <th
                  style={{
                    padding: '12px 16px',
                    textAlign: 'left',
                    fontWeight: 500,
                    color: 'var(--reviewer-text-secondary)',
                  }}
                  role="columnheader"
                >
                  {t('audits.colRisk')}
                </th>
                <th
                  onClick={() => handleSort('duration')}
                  style={{
                    padding: '12px 16px',
                    textAlign: 'center',
                    fontWeight: 500,
                    color: 'var(--reviewer-text-secondary)',
                    cursor: 'pointer',
                    userSelect: 'none',
                  }}
                  role="columnheader"
                  aria-sort={sortField === 'duration' ? (sortOrder === 'asc' ? 'ascending' : 'descending') : 'none'}
                >
                  {t('audits.colDuration')} {sortField === 'duration' && (sortOrder === 'asc' ? '↑' : '↓')}
                </th>
                <th
                  style={{
                    padding: '12px 16px',
                    textAlign: 'right',
                    fontWeight: 500,
                    color: 'var(--reviewer-text-secondary)',
                  }}
                  role="columnheader"
                >
                  {t('audits.colActions')}
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredAndSortedAudits.map((audit) => (
                <tr
                  key={audit.id}
                  style={{
                    borderBottom: '1px solid var(--reviewer-border-subtle)',
                    cursor: 'pointer',
                    transition: 'background-color 0.15s',
                  }}
                  onClick={() => navigate(`/audit/${audit.id}`)}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = 'var(--reviewer-bg-elevated)'
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent'
                  }}
                  role="row"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      navigate(`/audit/${audit.id}`)
                    }
                  }}
                  aria-label={t('audits.rowAria', { project: audit.project_name, status: audit.status, n: audit.total_findings })}
                >
                  <td style={{ padding: '12px 16px' }}>
                    <div>
                      <div style={{ 
                        fontWeight: 500,
                        color: 'var(--reviewer-text)',
                        marginBottom: '4px',
                      }}>
                        {audit.project_name}
                      </div>
                      <div style={{ 
                        fontSize: 'var(--reviewer-text-xs)',
                        color: 'var(--reviewer-text-tertiary)',
                      }}>
                        {audit.quick_scan && `${t('audits.quickScan')} • `}
                        {t('audits.tracksCount', { n: audit.tracks_executed.length })}
                      </div>
                    </div>
                  </td>
                  <td style={{ padding: '12px 16px', color: 'var(--reviewer-text-secondary)' }}>
                    {formatRelativeTime(audit.created_at, locale)}
                  </td>
                  <td style={{ padding: '12px 16px' }}>
                    <StatusBadge status={audit.status} size="sm" />
                  </td>
                  <td style={{ padding: '12px 16px', textAlign: 'center', color: 'var(--reviewer-text)' }}>
                    {audit.total_findings}
                  </td>
                  <td style={{ padding: '12px 16px' }}>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                      {audit.critical_count > 0 && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <SeverityBadge severity="critical" size="sm" />
                          <span style={{ fontSize: 'var(--reviewer-text-xs)', color: 'var(--reviewer-danger)' }}>
                            {audit.critical_count}
                          </span>
                        </div>
                      )}
                      {audit.high_count > 0 && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <SeverityBadge severity="high" size="sm" />
                          <span style={{ fontSize: 'var(--reviewer-text-xs)', color: 'var(--reviewer-warning)' }}>
                            {audit.high_count}
                          </span>
                        </div>
                      )}
                      {audit.critical_count === 0 && audit.high_count === 0 && audit.total_findings > 0 && (
                        <span style={{ fontSize: 'var(--reviewer-text-xs)', color: 'var(--reviewer-text-secondary)' }}>
                          Low risk
                        </span>
                      )}
                      {audit.total_findings === 0 && (
                        <span style={{ fontSize: 'var(--reviewer-text-xs)', color: 'var(--reviewer-success)' }}>
                          Clean
                        </span>
                      )}
                    </div>
                  </td>
                  <td style={{ padding: '12px 16px', textAlign: 'center', color: 'var(--reviewer-text-secondary)' }}>
                    {formatDuration(audit.duration_seconds)}
                  </td>
                  <td 
                    style={{ padding: '12px 16px', textAlign: 'right' }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <button
                      onClick={() => navigate(`/audit/${audit.id}`)}
                      style={{
                        padding: '4px 12px',
                        fontFamily: 'var(--reviewer-font-mono)',
                        fontSize: 'var(--reviewer-text-xs)',
                        backgroundColor: 'transparent',
                        border: '1px solid var(--reviewer-accent)',
                        color: 'var(--reviewer-accent)',
                        cursor: 'pointer',
                      }}
                      aria-label={`View audit details for ${audit.project_name}`}
                    >
                      View Details
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '12px',
            padding: '16px',
          }}
        >
          <button
            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
            disabled={currentPage === 1}
            style={{
              padding: '8px 16px',
              fontFamily: 'var(--reviewer-font-mono)',
              fontSize: 'var(--reviewer-text-sm)',
              backgroundColor: currentPage === 1 ? 'transparent' : 'var(--reviewer-bg)',
              border: '1px solid var(--reviewer-border)',
              color: currentPage === 1 ? 'var(--reviewer-text-tertiary)' : 'var(--reviewer-text)',
              cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
            }}
            aria-label={t('audits.prevAria')}
          >
            ← {t('common.previous')}
          </button>
          <span
            style={{
              fontFamily: 'var(--reviewer-font-mono)',
              fontSize: 'var(--reviewer-text-sm)',
              color: 'var(--reviewer-text-secondary)',
            }}
          >
            {t('common.pageOf', { page: currentPage, total: totalPages })}
          </span>
          <button
            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
            style={{
              padding: '8px 16px',
              fontFamily: 'var(--reviewer-font-mono)',
              fontSize: 'var(--reviewer-text-sm)',
              backgroundColor: currentPage === totalPages ? 'transparent' : 'var(--reviewer-bg)',
              border: '1px solid var(--reviewer-border)',
              color: currentPage === totalPages ? 'var(--reviewer-text-tertiary)' : 'var(--reviewer-text)',
              cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
            }}
            aria-label={t('audits.nextAria')}
          >
            {t('common.next')} →
          </button>
        </div>
      )}
      <StartAuditDialog isOpen={startOpen} onClose={() => setStartOpen(false)} />
    </div>
  )
}
